import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  deleteProgram,
  getAdminProgramPdfUrl,
  listPrograms,
  listProgramsFull,
  regenerateProgramPdf,
} from "../programsApi";
import { Program, ProgramListItem, RequirementCategory } from "../types";
import { SkeletonRow, SummaryCard } from "../components/Skeleton";

type StatusFilter = "all" | "published" | "draft";
type TypeFilter = "all" | "residence" | "passport";
type SortOption = "updatedDesc" | "updatedAsc" | "createdDesc" | "createdAsc" | "countryAsc" | "countryDesc";

const SORT_LABELS: Record<SortOption, string> = {
  updatedDesc: "最近更新",
  updatedAsc: "最早更新",
  createdDesc: "最近创建",
  createdAsc: "最早创建",
  countryAsc: "国家名称 A-Z",
  countryDesc: "国家名称 Z-A",
};

// Rows shown by the loading skeleton, and one width per table column
// (国家/项目, 类型, 状态, Slug, 图片, PDF, [actions]) -- picked to roughly
// match a typical result set so the table doesn't visibly resize once real
// rows swap in.
const SKELETON_ROWS = 8;
const SKELETON_WIDTHS = ["w-32", "w-16", "w-14", "w-24", "w-6", "w-20", "w-10"];

function formatDateTime(iso: string): string {
  return new Date(iso).toLocaleString("zh-CN", { hour12: false });
}

type ContentColumnKey =
  | "hero"
  | "overview"
  | "advantages"
  | "requirements"
  | "investment"
  | "process"
  | "cta";

// The "显示完整信息" toggle's extra columns -- every brochure field the edit
// page lets an admin fill in, laid out as columns of the same big table
// rather than a separate panel, so the whole thing scrolls as one table.
const CONTENT_COLUMNS: { key: ContentColumnKey; label: string }[] = [
  { key: "hero", label: "首页 Hero" },
  { key: "overview", label: "国家概况" },
  { key: "advantages", label: "项目优势" },
  { key: "requirements", label: "申请条件" },
  { key: "investment", label: "投资金额" },
  { key: "process", label: "申请流程" },
  { key: "cta", label: "CTA" },
];

// One category's preview -- title plus its list or lettered A/B/C options.
// Shared by a top-level category and by each category nested inside a
// section, in the "显示完整信息" requirements column.
function RequirementCategoryPreview({ category }: { category: RequirementCategory }) {
  return (
    <div>
      <p className="font-medium text-secondary-dark">{category.title}</p>
      {category.kind === "list" ? (
        <ul className="text-secondary-light">
          {(category.list?.items ?? []).map((item, j) => (
            <li key={j}>{category.list!.style === "numbered" ? `${j + 1}. ` : ""}{item}</li>
          ))}
        </ul>
      ) : (
        <div className="flex flex-col gap-1">
          {(category.options ?? []).map((option, oi) => (
            <div key={oi}>
              <p className="text-secondary-dark">
                {String.fromCharCode(65 + oi)}. {option.label}
              </p>
              <ul className="text-secondary-light">
                {option.list.items.map((item, j) => (
                  <li key={j}>{option.list.style === "numbered" ? `${j + 1}. ` : ""}{item}</li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// Returns null when the field is empty so the caller can fall back to a "—"
// placeholder instead of rendering an empty cell.
function renderContentCell(key: ContentColumnKey, p: Program): React.ReactNode | null {
  switch (key) {
    case "hero": {
      if (!p.heroBadge && !p.heroDescription && !(p.heroHighlights?.length ?? 0)) return null;
      return (
        <div className="flex flex-col gap-1">
          {p.heroBadge && <p className="font-medium text-secondary-dark">{p.heroBadge}</p>}
          {p.heroDescription && <p>{p.heroDescription}</p>}
          {(p.heroHighlights?.length ?? 0) > 0 && (
            <ul className="flex flex-wrap gap-1 mt-0.5">
              {p.heroHighlights!.map((h, i) => (
                <li key={i} className="bg-c-gray rounded-full px-2 py-0.5 text-[11px] whitespace-nowrap">
                  {h}
                </li>
              ))}
            </ul>
          )}
        </div>
      );
    }
    case "overview": {
      if (!(p.overviewDescription?.length ?? 0) && !(p.overviewStats?.length ?? 0)) return null;
      return (
        <div className="flex flex-col gap-1">
          {p.overviewDescription?.map((para, i) => (
            <p key={i}>{para}</p>
          ))}
          {(p.overviewStats?.length ?? 0) > 0 && (
            <ul className="flex flex-col gap-0.5 mt-1">
              {p.overviewStats!.map((s, i) => (
                <li key={i}>
                  <span className="text-secondary-light">{s.label}：</span>
                  {s.value}
                </li>
              ))}
            </ul>
          )}
        </div>
      );
    }
    case "advantages": {
      if (!(p.advantages?.length ?? 0)) return null;
      return (
        <ul className="flex flex-col gap-1.5">
          {p.advantages!.map((a, i) => (
            <li key={i}>
              <p className="font-medium text-secondary-dark">{a.title}</p>
              <p className="text-secondary-light">{a.description}</p>
            </li>
          ))}
        </ul>
      );
    }
    case "requirements": {
      if (!(p.requirementGroups?.length ?? 0)) return null;
      return (
        <div className="flex flex-col gap-2">
          {p.requirementGroups!.map((block, i) => {
            if (block.type === "note") {
              return <p key={i} className="italic text-secondary-light">{block.text}</p>;
            }
            if (block.type === "section") {
              return (
                <div key={i} className="flex flex-col gap-2 bg-gold/5 border border-gold/20 rounded-lg p-2">
                  <p className="font-semibold text-secondary-dark uppercase text-[11px] tracking-wide">{block.title}</p>
                  <div className="flex flex-col gap-2 pl-2 border-l-2 border-fourth-light">
                    {(block.items ?? []).map((item, ci) =>
                      item.type === "note" ? (
                        <p key={ci} className="italic text-secondary-light">{item.text}</p>
                      ) : (
                        <RequirementCategoryPreview category={item} key={ci} />
                      ),
                    )}
                  </div>
                </div>
              );
            }
            return <RequirementCategoryPreview category={block} key={i} />;
          })}
        </div>
      );
    }
    case "investment": {
      const hasInvestment =
        p.investmentTitle ||
        p.investmentNote ||
        (p.investmentTiers?.length ?? 0) > 0 ||
        (p.feeTables?.length ?? 0) > 0;
      if (!hasInvestment) return null;
      return (
        <div className="flex flex-col gap-1.5">
          {p.investmentTitle && <p className="font-medium text-secondary-dark">{p.investmentTitle}</p>}
          {p.investmentNote && <p className="text-secondary-light">{p.investmentNote}</p>}
          {(p.investmentTiers?.length ?? 0) > 0 && (
            <ul className="flex flex-col gap-0.5">
              {p.investmentTiers!.map((t, i) => (
                <li key={i}>
                  {t.label}：<span className="font-medium">{t.amount}</span>
                </li>
              ))}
            </ul>
          )}
          {p.feeTables?.map((table, i) => (
            <div key={i}>
              <p className="font-medium text-secondary-dark">{table.title}</p>
              <ul className="text-secondary-light">
                {table.rows.map((row, j) => (
                  <li key={j}>
                    {row.label}：{row.amount}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      );
    }
    case "process": {
      const hasProcess = p.processDurationLabel || (p.processSteps?.length ?? 0) > 0;
      if (!hasProcess) return null;
      return (
        <div className="flex flex-col gap-1">
          {p.processDurationLabel && (
            <p>
              总周期约 <span className="font-medium text-secondary-dark">{p.processDurationLabel}</span>
            </p>
          )}
          {(p.processSteps?.length ?? 0) > 0 && (
            <ol className="flex flex-col gap-0.5">
              {p.processSteps!.map((s, i) => (
                <li key={i}>
                  {i + 1}. <span className="font-medium text-secondary-dark">{s.title}</span> — {s.description}
                </li>
              ))}
            </ol>
          )}
        </div>
      );
    }
    case "cta": {
      if (!p.ctaTitle && !p.ctaDescription) return null;
      return (
        <div className="flex flex-col gap-1">
          {p.ctaTitle && <p className="font-medium text-secondary-dark">{p.ctaTitle}</p>}
          {p.ctaDescription && <p className="text-secondary-light">{p.ctaDescription}</p>}
        </div>
      );
    }
    default:
      return null;
  }
}

export default function AdminProgramListPage() {
  const [programs, setPrograms] = useState<ProgramListItem[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState<StatusFilter>("all");
  const [typeFilter, setTypeFilter] = useState<TypeFilter>("all");
  const [pdfLoadingId, setPdfLoadingId] = useState<string | null>(null);
  const [regeneratingId, setRegeneratingId] = useState<string | null>(null);
  const [sortBy, setSortBy] = useState<SortOption>("updatedDesc");
  const [showAllInfo, setShowAllInfo] = useState(false);
  const [fullPrograms, setFullPrograms] = useState<Record<string, Program> | null>(null);
  const [fullLoading, setFullLoading] = useState(false);
  const navigate = useNavigate();

  async function refresh() {
    try {
      setPrograms(await listPrograms());
    } catch (err) {
      setError(err instanceof Error ? err.message : "加载失败");
    }
  }

  useEffect(() => {
    refresh();
  }, []);

  const loading = programs === null;

  // Fetches every program's full content once, the first time "显示完整信息"
  // is turned on -- toggling it back off just hides the panels again rather
  // than discarding the fetched data, so re-enabling it is instant.
  async function handleToggleShowAllInfo() {
    if (!showAllInfo && !fullPrograms) {
      setFullLoading(true);
      try {
        const list = await listProgramsFull();
        setFullPrograms(Object.fromEntries(list.map((p) => [p.id, p])));
      } catch (err) {
        setError(err instanceof Error ? err.message : "加载完整信息失败");
        return;
      } finally {
        setFullLoading(false);
      }
    }
    setShowAllInfo((v) => !v);
  }

  const summary = useMemo(() => {
    const list = programs ?? [];
    return {
      total: list.length,
      published: list.filter((p) => p.status === "published").length,
      draft: list.filter((p) => p.status === "draft").length,
      residence: list.filter((p) => p.programType === "residence").length,
      passport: list.filter((p) => p.programType === "passport").length,
    };
  }, [programs]);

  const filtered = useMemo(() => {
    const list = programs ?? [];
    const q = search.trim().toLowerCase();
    return list.filter((p) => {
      if (statusFilter !== "all" && p.status !== statusFilter) return false;
      if (typeFilter !== "all" && p.programType !== typeFilter) return false;
      if (q) {
        const haystack = `${p.country} ${p.countryEn} ${p.programTitle} ${p.slug}`.toLowerCase();
        if (!haystack.includes(q)) return false;
      }
      return true;
    });
  }, [programs, search, statusFilter, typeFilter]);

  const sorted = useMemo(() => {
    const list = [...filtered];
    switch (sortBy) {
      case "updatedAsc":
        return list.sort((a, b) => (a.updatedAt ?? "").localeCompare(b.updatedAt ?? ""));
      case "createdDesc":
        return list.sort((a, b) => (b.createdAt ?? "").localeCompare(a.createdAt ?? ""));
      case "createdAsc":
        return list.sort((a, b) => (a.createdAt ?? "").localeCompare(b.createdAt ?? ""));
      case "countryAsc":
        return list.sort((a, b) => a.country.localeCompare(b.country, "zh-CN"));
      case "countryDesc":
        return list.sort((a, b) => b.country.localeCompare(a.country, "zh-CN"));
      case "updatedDesc":
      default:
        return list.sort((a, b) => (b.updatedAt ?? "").localeCompare(a.updatedAt ?? ""));
    }
  }, [filtered, sortBy]);

  async function handleDelete(program: ProgramListItem) {
    if (!window.confirm(`确定要删除「${program.country} · ${program.programTitle}」吗？此操作不可撤销。`)) {
      return;
    }
    await deleteProgram(program.id);
    refresh();
  }

  async function handleDownloadPdf(program: ProgramListItem) {
    setPdfLoadingId(program.id);
    try {
      const url = await getAdminProgramPdfUrl(program.id);
      window.open(url, "_blank");
    } catch (err) {
      alert(err instanceof Error ? err.message : "PDF 生成失败");
    } finally {
      setPdfLoadingId(null);
    }
  }

  // Forces a fresh render even if the cached PDF's key would otherwise still
  // be considered valid (e.g. after a print-template change, not a content
  // edit) — see pdf.service.ts's regenerateProgramPdf. Re-fetches the list
  // afterward so the "上次更新" timestamp shown comes from the real,
  // persisted pdfGeneratedAt value (survives a page reload) rather than a
  // client-side-only confirmation that disappears after a few seconds.
  async function handleUpdatePdf(program: ProgramListItem) {
    setRegeneratingId(program.id);
    try {
      await regenerateProgramPdf(program.id);
      await refresh();
    } catch (err) {
      alert(err instanceof Error ? err.message : "PDF 更新失败");
    } finally {
      setRegeneratingId(null);
    }
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-semibold text-secondary-dark">项目管理</h1>
        <button
          onClick={() => navigate("/admin/programs/new")}
          className="bg-gold hover:bg-gold-light text-secondary-dark font-medium rounded-full px-5 py-2 text-sm transition-colors"
        >
          + 新建项目
        </button>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 mb-6">
        <SummaryCard label="全部项目" value={loading ? null : summary.total} />
        <SummaryCard label="已发布" value={loading ? null : summary.published} />
        <SummaryCard label="草稿" value={loading ? null : summary.draft} />
        <SummaryCard label="居留项目" value={loading ? null : summary.residence} />
        <SummaryCard label="护照项目" value={loading ? null : summary.passport} />
      </div>

      <div className="flex flex-wrap items-center gap-3 mb-4">
        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="搜索国家 / 项目名 / slug"
          className="border border-fourth-light rounded-lg px-3 py-1.5 text-sm outline-none focus:border-gold transition-colors bg-white w-64"
        />
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value as StatusFilter)}
          className="border border-fourth-light rounded-lg px-3 py-1.5 text-sm outline-none focus:border-gold transition-colors bg-white"
        >
          <option value="all">全部状态</option>
          <option value="published">已发布</option>
          <option value="draft">草稿</option>
        </select>
        <select
          value={typeFilter}
          onChange={(e) => setTypeFilter(e.target.value as TypeFilter)}
          className="border border-fourth-light rounded-lg px-3 py-1.5 text-sm outline-none focus:border-gold transition-colors bg-white"
        >
          <option value="all">全部类型</option>
          <option value="residence">居留项目</option>
          <option value="passport">护照项目</option>
        </select>
        <select
          value={sortBy}
          onChange={(e) => setSortBy(e.target.value as SortOption)}
          className="border border-fourth-light rounded-lg px-3 py-1.5 text-sm outline-none focus:border-gold transition-colors bg-white"
        >
          {(Object.keys(SORT_LABELS) as SortOption[]).map((key) => (
            <option key={key} value={key}>
              排序：{SORT_LABELS[key]}
            </option>
          ))}
        </select>
        <button
          onClick={handleToggleShowAllInfo}
          disabled={fullLoading}
          className={`px-3 py-1.5 rounded-lg text-sm border transition-colors disabled:opacity-60 ${
            showAllInfo
              ? "bg-secondary-dark text-white border-secondary-dark"
              : "bg-white border-fourth-light text-secondary-dark/70 hover:border-gold"
          }`}
        >
          {fullLoading ? "加载中…" : showAllInfo ? "收起完整信息" : "显示完整信息"}
        </button>
        {programs && (
          <span className="text-xs text-secondary-light">
            显示 {filtered.length} / {programs.length} 项
          </span>
        )}
      </div>

      {error && <p className="text-red-600 text-sm mb-4">{error}</p>}

      <div className="bg-white rounded-xl border border-fourth-light overflow-hidden">
        <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-c-gray text-left text-secondary-light uppercase text-xs tracking-wide">
              <th className="px-4 py-3">国家 / 项目</th>
              <th className="px-4 py-3">类型</th>
              <th className="px-4 py-3">状态</th>
              <th className="px-4 py-3">Slug</th>
              <th className="px-4 py-3">图片</th>
              <th className="px-4 py-3">PDF</th>
              {showAllInfo &&
                CONTENT_COLUMNS.map((c) => (
                  <th key={c.key} className="px-3 py-3 whitespace-nowrap">
                    {c.label}
                  </th>
                ))}
              <th className="px-4 py-3"></th>
            </tr>
          </thead>
          <tbody>
            {loading &&
              Array.from({ length: SKELETON_ROWS }).map((_, i) => (
                <SkeletonRow key={i} widths={SKELETON_WIDTHS} />
              ))}
            {programs !== null && filtered.length === 0 && (
              <tr>
                <td
                  colSpan={7 + (showAllInfo ? CONTENT_COLUMNS.length : 0)}
                  className="px-4 py-8 text-center text-secondary-light"
                >
                  {programs.length === 0 ? "还没有任何项目，点击右上角「新建项目」开始。" : "没有匹配当前筛选条件的项目。"}
                </td>
              </tr>
            )}
            {sorted.map((program) => (
              <tr key={program.id} className="border-t border-fourth-light hover:bg-c-gray/60">
                  <td className="px-4 py-3">
                    <div className="font-medium text-secondary-dark">{program.country}</div>
                    <div className="text-secondary-light text-xs">{program.programTitle}</div>
                  </td>
                  <td className="px-4 py-3">
                    {program.programType === "passport" ? "护照项目" : "居留项目"}
                  </td>
                  <td className="px-4 py-3">
                    <span
                      className={`px-2 py-0.5 rounded-full text-xs ${
                        program.status === "published"
                          ? "bg-green-100 text-green-700"
                          : "bg-gray-100 text-gray-600"
                      }`}
                    >
                      {program.status === "published" ? "已发布" : "草稿"}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-secondary-light">{program.slug}</td>
                  <td className="px-4 py-3 text-secondary-light">{program.imageCount}</td>
                  <td className="px-4 py-3">
                    <div className="flex flex-col gap-1.5">
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => handleDownloadPdf(program)}
                          disabled={pdfLoadingId === program.id}
                          className="text-gold hover:text-secondary-dark transition-colors disabled:opacity-50 text-xs border border-gold/40 rounded-full px-3 py-1 whitespace-nowrap"
                        >
                          {pdfLoadingId === program.id ? "生成中…" : "下载 PDF"}
                        </button>
                        <button
                          onClick={() => handleUpdatePdf(program)}
                          disabled={regeneratingId === program.id}
                          className="text-secondary-dark/70 hover:text-secondary-dark transition-colors disabled:opacity-50 text-xs border border-fourth-light rounded-full px-3 py-1 whitespace-nowrap"
                        >
                          {regeneratingId === program.id ? "更新中…" : "更新 PDF"}
                        </button>
                      </div>
                      {program.pdfGeneratedAt && (
                        <span className="text-[11px] text-secondary-light whitespace-nowrap">
                          上次更新：{formatDateTime(program.pdfGeneratedAt)}
                        </span>
                      )}
                    </div>
                  </td>
                  {showAllInfo &&
                    CONTENT_COLUMNS.map((c) => {
                      const full = fullPrograms?.[program.id];
                      return (
                        <td
                          key={c.key}
                          className="align-top px-3 py-3 min-w-[220px] max-w-[280px] whitespace-normal break-words text-xs text-secondary-dark/80 leading-relaxed"
                        >
                          {!fullPrograms ? (
                            <span className="block h-3 w-full rounded bg-c-gray animate-pulse" />
                          ) : (
                            (full && renderContentCell(c.key, full)) || (
                              <span className="text-secondary-light">—</span>
                            )
                          )}
                        </td>
                      );
                    })}
                  <td className="px-4 py-3 text-right whitespace-nowrap">
                    <Link
                      to={`/admin/programs/${program.id}`}
                      className="text-gold hover:text-secondary-dark transition-colors mr-4"
                    >
                      编辑
                    </Link>
                    <button
                      onClick={() => handleDelete(program)}
                      className="text-red-500 hover:text-red-700 transition-colors"
                    >
                      删除
                    </button>
                  </td>
                </tr>
            ))}
          </tbody>
        </table>
        </div>
      </div>
    </div>
  );
}
